import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "401",
  "slug": "how-to-tell-which-hemisphere-you-are-in",
  "title": "How to Tell Which Hemisphere You're In From the Sky and the Landscape",
  "excerpt": "Drop into an unfamiliar place with no signs to read, which half of the planet are you on? The sun, the stars, and even satellite dishes give it away.",
  "date": "2026-06-10",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1595520519880-a86c48ea536c?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzdGFycnklMjBuaWdodCUyMHNreXxlbnwxfDB8fHwxNzgxMDc5MjIyfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "Check where the midday sun sits (toward the south in the Northern Hemisphere, toward the north in the Southern), which way satellite dishes point, and which stars are out, Polaris up north, the Southern Cross down south.",
  "content": [
    {
      "type": "paragraph",
      "text": "Imagine being dropped somewhere on Earth with no signs, no language, and no map, just the view around you. One of the first and most useful questions to answer is which hemisphere you are in. Get that right and you have instantly cut the planet in half. The good news is that the sky and the landscape are full of clues, if you know how to read them."
    },
    {
      "type": "heading",
      "text": "Follow the Sun"
    },
    {
      "type": "paragraph",
      "text": "The sun is your most reliable guide. In the Northern Hemisphere, the midday sun always sits somewhere to the south, so shadows at noon point north. In the Southern Hemisphere, it is the mirror image: the midday sun sits to the north and shadows point south. If you can watch the sun's arc across the sky, that north-or-south lean tells you which half of the world you are standing on."
    },
    {
      "type": "heading",
      "text": "Read the Night Sky"
    },
    {
      "type": "paragraph",
      "text": "The stars are just as telling. In the Northern Hemisphere you can find Polaris, the North Star, which sits almost directly over the North Pole and barely moves, along with the easily spotted Big Dipper. The Southern Hemisphere has no bright pole star at all; instead, the night sky features the Southern Cross, a compact constellation used for navigation for centuries. Spot one or the other and you know exactly where you are."
    },
    {
      "type": "heading",
      "text": "Which Way the Satellite Dishes Point"
    },
    {
      "type": "paragraph",
      "text": "Here is a clue beloved by location-guessing experts: satellite television dishes. Most broadcast satellites sit in orbit above the equator, so dishes are aimed toward it. In the Northern Hemisphere that means dishes generally tilt toward the south, while in the Southern Hemisphere they tilt toward the north. A row of dishes all leaning the same way is a quiet but reliable signpost."
    },
    {
      "type": "heading",
      "text": "Seasons and Vegetation Out of Sync"
    },
    {
      "type": "paragraph",
      "text": "The hemispheres run on opposite calendars. When it is summer in Europe and North America, it is winter in Australia, Argentina, and South Africa. So if you see lush, green, high-summer growth and you have other reasons to think it is the middle of the year, you are likely in the north; if everything looks like deep summer in December, you have probably crossed into the south. Combine seasonal cues with latitude clues from plants and climate and the picture sharpens fast."
    },
    {
      "type": "heading",
      "text": "The Myth You Can Safely Ignore"
    },
    {
      "type": "paragraph",
      "text": "You may have heard that water spirals down a sink or toilet in opposite directions depending on the hemisphere, supposedly thanks to the Coriolis effect. It is a charming idea and completely false at that scale. The direction water drains is decided by the shape of the basin and how the water was set moving, not by which hemisphere you are in. The Coriolis effect is real and important for hurricanes and jet streams, but far too weak to steer your bathroom plumbing."
    },
    {
      "type": "list",
      "items": [
        "Midday sun and shadows: sun toward the south in the north, toward the north in the south",
        "Stars: Polaris and the Big Dipper in the north, the Southern Cross in the south",
        "Satellite dishes: tilted toward the equator, so south in the north and north in the south",
        "Seasons: reversed, with summer and winter on opposite calendars"
      ]
    }
  ]
};
